import { EmptyIcon } from '@/assets/icons';
import { ImagesMobile } from '@/assets/images';
import { Button, Checkbox } from '@/components';
import {
	DocumentTypeEnum,
	FileTypeEnum,
	PageSize,
	ROUTERS,
	TableTypes,
} from '@/constant';
import {
	useDeleteListSubmit,
	useGetListData,
	useRowSelection,
	useSearchFilterSubmit,
	useSearchParams,
} from '@/hooks';
import { TValueLodging } from '@/pages/workers/lm1-02/lm1-02.config';
import { RootState, useAppDispatch, WorkerLodgingActions } from '@/stores';
import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { DefaultOptionType } from 'antd/es/select';
import { cls, showAlert } from 'utils';

import { isNilOrEmpty } from 'ramda-adjunct';

import PagesizeFilter from '../components/pagesize-filter';
import CPagination from './pagination';
import { ITableMobile } from './type';

export const TableMobile = ({
	dataMobile,
	isSearching,
	searchFilterRef,
	searchFilterType,
	title,
	tableTypes,
	toDetailPathName,
	paginationClassName,
	tableClassName,
	isShowCheckbox,
	handleDelete,
	isShowDeleteButton,
	isHideTotal,
	totalRecord,
	pageSize,
	labelBtnCreate,
	handleAddItem,
	isShowBtnAdd,
	isChooseOne,
	labelBtnAdd,
	listIdChecked,
	isShowNo,
	dataState,
	isShowCheckAll = true,
	pageDefault,
	renderRightAction,
	...rest
}: ITableMobile) => {
	const navigate = useNavigate();
	const [_, setSearchParams] = useSearchParams();
	const dispatch = useAppDispatch();
	const [page, setPage] = useState(1);
	const selectParams = useSelector(
		(state: RootState) => searchFilterType && state[searchFilterType].params
	);

	const {
		total: totalCommon,
		params,
		list,
	} = useGetListData({
		type: tableTypes,
	});

	const { selectedRowKeys, setState, onReset } = useRowSelection();

	const transformedData = useMemo(() => {
		if (!dataMobile) return [];
		return dataMobile.map((item) => {
			const transformedItem: Record<string, any> = {};
			Object.entries(item).forEach(([key, value]) => {
				transformedItem[key] = value.value;
			});
			return transformedItem;
		});
	}, [dataMobile]);

	const isCheckAll = useMemo(
		() => transformedData?.every((item) => selectedRowKeys.includes(item.id)),
		[selectedRowKeys, transformedData]
	);

	const pageSizeTable = useMemo(() => {
		return tableTypes ? params?.pageSize : pageSize ?? PageSize.XXXS;
	}, [selectParams, pageSize, params?.pageSize]);

	const total = useMemo(() => {
		return tableTypes ? totalCommon : totalRecord ?? 0;
	}, [totalCommon, tableTypes, totalRecord]);

	const { onSubmit } = useSearchFilterSubmit({
		searchFilterType,
		pagination: { ...selectParams },
	});

	useEffect(() => {
		if (pageDefault) {
			setPage(pageDefault);
		} else {
			setPage(params?.page || 1);
		}
	}, [params?.page, pageDefault]);

	useEffect(() => {
		if (listIdChecked) {
			const newSelectedRowKeys = listIdChecked || [];
			setState({ selectedRowKeys: newSelectedRowKeys });
		}
	}, [listIdChecked]);

	const handleCheckAll = useCallback(() => {
		const currentPageIds = transformedData.map((item: any) => Number(item?.id));

		let newSelectedRowKeys: number[];

		if (!isCheckAll) {
			const otherPagesSelectedKeys = (selectedRowKeys as number[]).filter(
				(key) => !currentPageIds.includes(key)
			);

			newSelectedRowKeys = [...otherPagesSelectedKeys, ...currentPageIds];
		} else {
			newSelectedRowKeys = (selectedRowKeys as number[]).filter(
				(key) => !currentPageIds.includes(key)
			);
		}

		setState({ selectedRowKeys: newSelectedRowKeys });
	}, [transformedData, isCheckAll]);

	const handleCheckbox = useCallback(
		(id: number, checked: boolean) => {
			setState({
				selectedRowKeys: checked
					? isChooseOne
						? [id]
						: [...selectedRowKeys, id]
					: selectedRowKeys.filter((key) => key !== id),
			});
		},
		[selectedRowKeys, isChooseOne]
	);

	const _onChangePagination = useCallback(
		(current: number, pageSize?: number) => {
			onSubmit({
				...params,
				page: current,
				pageSize: pageSize || PageSize.S,
			});
			rest?.onChangePagination?.(current, pageSize);
			setPage(current);
		},
		[onSubmit, params]
	);

	const _onPageSizeChange = useCallback(
		(
			value: DefaultOptionType['value'],
			option: DefaultOptionType | DefaultOptionType[]
		) => {
			onSubmit({
				...params,
				page: 1,
				pageSize: (value as PageSize) || PageSize.S,
			});

			searchFilterRef?.current?.onPageSizeChange?.(value, option);
			rest?.onPageSizeChange?.(value as PageSize);
		},
		[searchFilterRef?.current, params]
	);

	const handleClickDetail = useCallback(
		(id: number) => {
			const item: any = (list as any)?.find((item: any) => item?.id === id);
			if (toDetailPathName) {
				if (
					(tableTypes === TableTypes.DM1_01 &&
						item?.fileType === FileTypeEnum.FORM &&
						item?.fileDocumentType !== DocumentTypeEnum.C_54) ||
					(toDetailPathName === ROUTERS.DM1_03 &&
						item?.type === FileTypeEnum.FORM &&
						item?.documentType !== DocumentTypeEnum.C_54)
				) {
					showAlert(
						'모바일에서 지원하지 않는 파일입니다.\nPC에서 확인해주세요.'
					);
				} else {
					navigate(toDetailPathName?.replace(':id', id.toString()));
					dispatch(WorkerLodgingActions.saveDataLodging({} as TValueLodging));
				}
			}
		},
		[toDetailPathName, list]
	);

	const handleClickToRegister = useCallback(() => {
		if (rest.toCreatePathName) {
			navigate(rest.toCreatePathName, { state: dataState || {} });
			if (dataState) {
				dispatch(WorkerLodgingActions.saveDataLodging(dataState?.providerInfo));
			}
		} else {
			rest.navigateRegister?.();
		}
	}, [rest.toCreatePathName, dataState]);

	const { onDeleteSubmit } = useDeleteListSubmit({
		tableType: tableTypes,
		searchFilterRef,
		listIds: selectedRowKeys as number[],
	});

	const handleClickDelete = useCallback(() => {
		if (isNilOrEmpty(dataMobile)) {
			showAlert('삭제할 보유 숙소를(을) 선택해주세요.');
			return;
		}
		if (handleDelete) {
			handleDelete?.(selectedRowKeys as number[], () => {
				onReset();
				setSearchParams({
					page: 1,
				});
			});
		} else {
			onDeleteSubmit(() => {
				onReset();
				setSearchParams({
					page: 1,
				});
			});
		}
	}, [onDeleteSubmit, selectedRowKeys, handleDelete, dataMobile]);

	const onClickButton = useCallback(() => {
		handleAddItem?.(selectedRowKeys as number[], () => {
			onReset();
			setSearchParams({
				page: 1,
			});
		});
	}, [selectedRowKeys]);

	return (
		<div
			className={cls(
				`sm:mx-0 rounded-t-[6.111vw] pb-[100px] md:pb-[160px] md:px-[34px]`,
				tableClassName
			)}
		>
			{!isHideTotal && (
				<div className="relative flex flex-row justify-between items-center gap-3 md:mb-[8px]">
					<p className="text-[16px] md:text-[20px] font-semibold text-font2">
						{title}
						<span className="text-blue8"> {total?.toLocaleString('en')}</span>개
					</p>

					<div className="flex flex-row items-center gap-2">
						{renderRightAction && renderRightAction()}
						<PagesizeFilter
							onPageSizeChange={_onPageSizeChange}
							defaultPageSize={pageSizeTable}
							containerClassName="relative top-0"
						/>
					</div>
				</div>
			)}
			{isShowDeleteButton && (
				<div
					className={`flex ${isShowCheckAll ? 'justify-between' : 'justify-end'}  items-center mt-[20px]`}
				>
					{isShowCheckAll && (
						<div className="flex items-center gap-2">
							<Checkbox
								checked={isCheckAll}
								className=""
								onChange={handleCheckAll}
								disabled={isNilOrEmpty(dataMobile)}
							/>
							<label className="text-[13px] font-normal text-font2">
								전체 선택
							</label>
						</div>
					)}
					<div className="flex gap-2">
						<Button
							className="bg-white text-cornflower border-cornflower min-w-[55px] h-[28px] md:h-[32px] text-[13px]"
							onClick={handleClickDelete}
						>
							삭제
						</Button>
						{(rest.toCreatePathName || rest?.navigateRegister) && (
							<Button
								className="bg-cornflower text-white border-cornflower min-w-[78px] h-[28px] md:h-[32px] text-[13px]"
								onClick={handleClickToRegister}
							>
								{labelBtnCreate}
							</Button>
						)}
					</div>
				</div>
			)}

			{dataMobile?.length > 0 ? (
				<div className="pb-0 overflow-auto md:p-0 sm:pb-10">
					{dataMobile?.map((item, index) => {
						return (
							<div
								className={cls(
									'inline-block w-full relative border-b-[1px] border-softPeach last-of-type:border-none py-[20px]',
									rest.customClassItem
								)}
								key={index}
							>
								{isShowCheckbox && (
									<div className="inline-block relative mr-2 mb-[12px]">
										<Checkbox
											checked={selectedRowKeys?.includes(
												Number(item?.id?.value)
											)}
											className=""
											onChange={(e) =>
												handleCheckbox(
													Number(item?.id?.value),
													e.target.checked
												)
											}
										/>
									</div>
								)}
								{Object.entries(item).map(([key, items]) => {
									return (
										<div
											className={`items-center text-font2 text-[13px] font-normal inline-block last-of-type:mb-0 ${items?.className} ${key === 'id' ? 'mb-[12px]' : 'mb-[6px]'}`}
											key={key}
											onClick={() => handleClickDetail(Number(item?.id?.value))}
										>
											<div className={cls(items?.containerClassName)}>
												{items?.label && (
													<span className={cls('label', items?.classNameLabel)}>
														{items?.label}
													</span>
												)}
												{items?.callback ? (
													items?.callback?.()
												) : (
													<span className="break-all">
														{key === 'id' && isShowNo
															? total - (page - 1) * pageSizeTable - index
															: items?.value
																? items?.value
																: '-'}
													</span>
												)}
											</div>
											{items?.subValue && (
												<span className={cls(' ml-1')}>{items?.subValue}</span>
											)}
											{toDetailPathName && (
												<img
													src={ImagesMobile.iconMoreTable}
													alt=""
													className="w-[18px] h-[18px] absolute right-0 top-1/2 translate-y-[-50%]"
												/>
											)}
										</div>
									);
								})}
							</div>
						);
					})}
				</div>
			) : (
				<div
					className={`flex flex-col justify-center items-center py-5 gap-2 absolute left-[50%] top-[50%] translate-x-[-50%] translate-y-[-50%] ${isShowDeleteButton ? 'pt-[16.667vw]' : ''}`}
				>
					<EmptyIcon />
					<p className="text-center text-font8 text-sm">
						{searchFilterRef?.current?.isSearching || isSearching
							? '조회된 결과가 없습니다.'
							: '정보가 없습니다.'}
					</p>
				</div>
			)}
			{total > pageSizeTable && (
				<div
					className={`fixed bottom-0 left-0 px-[18px] md:px-[52px] w-full bg-white pt-[12px] md:py-[20px] pb-[34px] shadow-pagination ${paginationClassName}`}
				>
					<CPagination
						current={page}
						onChange={_onChangePagination}
						total={total}
						showSizeChanger={false}
						hideOnSinglePage
						className="text-[13px]"
						pageSize={pageSizeTable}
					/>
				</div>
			)}
			{isShowBtnAdd && (
				<Button
					className="fixed bottom-0 w-full left-0 h-[60px] bg-menu text-white hover:!bg-menu hover:!text-white rounded-none border-0"
					onClick={onClickButton}
				>
					{labelBtnAdd}
				</Button>
			)}
		</div>
	);
};

export default memo(TableMobile);
